<template>
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 z-[9999]"
      @click.self="handleClose"
    >
      <div
        class="bg-white rounded-2xl my-4 shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black max-w-md w-full transform transition-all"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-6 pt-4">
          <h2 class="text-2xl font-bold text-gray-900">Edit Certifications</h2>
          <button
            @click="handleClose"
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>

        <!-- Content -->
        <div class="px-8 py-6 space-y-6 max-h-[60vh] overflow-y-auto">
          <!-- Loop through each certification -->
          <div 
            v-for="(item, index) in localItems" 
            :key="index"
            class="space-y-4 pb-6 border-b-2 border-gray-200 last:border-b-0"
          >
            <!-- Header with remove button -->
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-bold text-gray-900">
                Certification {{ index + 1 }}
              </h3>
              <button
                v-if="localItems.length > 1"
                @click="removeItem(index)"
                class="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                title="Remove certification"
              >
                <XMarkIcon class="w-5 h-5" />
              </button>
            </div>

            <!-- Certification Type -->
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700">
                Certification Type
              </label>
              <div class="relative">
                <select
                  v-model="item.type"
                  class="w-full h-12 pl-10 pr-4 bg-white rounded-lg border-2 border-gray-200 appearance-none text-gray-900 focus:outline-none focus:border-blue-500 transition-colors"
                >
                  <option value="" disabled>Select certification type...</option>
                  <option v-for="type in certificationTypes" :key="type" :value="type">
                    {{ type }}
                  </option>
                </select>
                <BookOpenIcon class="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                <ChevronDownIcon class="absolute right-3 top-3.5 w-5 h-5 text-gray-400" />
              </div>
            </div>

            <!-- Certification Name -->
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700">
                Certification Name
              </label>
              <div class="relative">
                <input
                  type="text"
                  v-model="item.name"
                  class="w-full h-12 pl-10 pr-4 bg-white rounded-lg border-2 border-gray-200 text-gray-900 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Enter certification name"
                />
                <DocumentTextIcon class="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              </div>
            </div>

            <!-- Issuing Organization -->
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700">
                Issuing Organization
              </label>
              <div class="relative">
                <input
                  type="text"
                  v-model="item.organization"
                  class="w-full h-12 pl-10 pr-4 bg-white rounded-lg border-2 border-gray-200 text-gray-900 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Enter organization name"
                />
                <BuildingOffice2Icon class="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              </div>
            </div>

            <!-- Additional Details -->
            <div class="space-y-2">
              <label class="block text-sm font-semibold text-gray-700">
                Additional Details
              </label>
              <div class="relative">
                <input
                  type="text"
                  v-model="item.details"
                  class="w-full h-12 pl-10 pr-4 bg-white rounded-lg border-2 border-gray-200 text-gray-900 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Date issued, License number, etc. (Optional)"
                />
                <InformationCircleIcon class="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              </div>
            </div>

            <!-- Preview for this certification entry -->
            <div v-if="item.name && item.organization" class="p-3 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-lg">
              <div class="flex items-start gap-3">
                <div class="flex-shrink-0 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                  <ShieldCheckIcon class="w-5 h-5 text-white" />
                </div>
                <div>
                  <p class="text-xs font-semibold text-yellow-900 mb-1">Preview</p>
                  <p class="text-sm font-bold text-yellow-700">{{ item.name }} - {{ item.organization }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Add Another Certification Button -->
          <button
            @click="addItem"
            class="w-full py-3 text-blue-600 bg-blue-50 border-2 border-blue-200 border-dashed rounded-lg hover:bg-blue-100 hover:border-blue-400 transition-colors font-medium flex items-center justify-center gap-2"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Another Certification
          </button>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 p-6">
          <button
            @click="handleClose"
            class="px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            @click="handleSave"
            :disabled="!isValid || isSaving"
            class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-md"
          >
            {{ isSaving ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { 
  XMarkIcon,
  BookOpenIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  InformationCircleIcon,
  ChevronDownIcon,
  ShieldCheckIcon
} from '@heroicons/vue/24/outline';

interface CertificationItem {
  type: string;
  name: string;
  organization: string;
  details: string;
}

interface Props {
  isOpen: boolean;
  items: CertificationItem[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save', items: CertificationItem[]): void;
}>();

const certificationTypes = [
  'Professional License',
  'Academic Certificate',
  'Teaching Certificate',
  'Language Proficiency',
  'Skills Certificate',
  'Industry Certification',
  'Other'
];

const localItems = ref<CertificationItem[]>(props.items?.map(item => ({ ...item })) || [{
  type: '',
  name: '',
  organization: '',
  details: ''
}]);
const isSaving = ref(false);

const isValid = computed(() => {
  return localItems.value.every(item => 
    item.type.trim().length > 0 && 
    item.name.trim().length > 0 && 
    item.organization.trim().length > 0
  );
});

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    localItems.value = props.items?.length ? 
      props.items.map(item => ({ ...item })) : 
      [{ type: '', name: '', organization: '', details: '' }];
  }
});

const addItem = () => {
  localItems.value.push({
    type: '',
    name: '',
    organization: '',
    details: ''
  });
};

const removeItem = (index: number) => {
  localItems.value.splice(index, 1);
};

const handleSave = () => {
  if (!isValid.value) return;
  
  isSaving.value = true;
  const validItems = localItems.value.filter(item => 
    item.type.trim().length > 0 && 
    item.name.trim().length > 0 && 
    item.organization.trim().length > 0
  );
  emit('save', validItems);
  isSaving.value = false;
};

const handleClose = () => {
  emit('close');
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.9);
}
</style>